import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';
import { updateCampaign } from '@/services/campaigns';
import type { Campaign } from '@/types/api';

interface EditCampaignDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
  onSaved?: (campaign: Campaign) => void;
}

const OBJECTIVE_OPTIONS = [
  { value: 'brand-awareness', label: 'Brand Awareness' },
  { value: 'consideration', label: 'Consideration' },
  { value: 'driving-purchase', label: 'Driving Purchase' },
];

export function EditCampaignDetailsModal({
  isOpen,
  onClose,
  campaign,
  onSaved,
}: EditCampaignDetailsModalProps) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [objective, setObjective] = useState(OBJECTIVE_OPTIONS[0].value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(campaign?.name ?? '');
    setStartDate('');
    setEndDate('');
    setObjective(OBJECTIVE_OPTIONS[0].value);
    setError('');
  }, [isOpen, campaign?.id, campaign?.name]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!campaign?.id) return;
    if (!name.trim()) {
      setError('Campaign name is required.');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const updated = await updateCampaign(campaign.id, { name: name.trim() });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Campaign Details"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSave} className="space-y-6">
        {error && (
          <div className="p-3 rounded-lg bg-error-container text-on-error-container text-sm">
            {error}
          </div>
        )}
        <div className="space-y-2">
          <label
            className="text-[11px] uppercase font-black tracking-widest text-on-surface-variant block"
            htmlFor="edit-campaign-name"
          >
            Campaign Name
          </label>
          <input
            className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl px-4 py-4 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-on-surface-variant/40 placeholder:font-normal"
            id="edit-campaign-name"
            placeholder="Summer Collection 2024"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label
              className="text-[10px] uppercase font-black tracking-widest text-on-surface-variant block"
              htmlFor="edit-start-date"
            >
              Start Date
            </label>
            <div className="relative">
              <Icon
                name="calendar_today"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-sm pointer-events-none"
              />
              <input
                className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-on-surface"
                id="edit-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label
              className="text-[10px] uppercase font-black tracking-widest text-on-surface-variant block"
              htmlFor="edit-end-date"
            >
              End Date
            </label>
            <div className="relative">
              <Icon
                name="event_busy"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-sm pointer-events-none"
              />
              <input
                className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-on-surface"
                id="edit-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label
            className="text-[11px] uppercase font-black tracking-widest text-on-surface-variant block"
            htmlFor="edit-objective"
          >
            Primary objective
          </label>
          <div className="relative">
            <select
              className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none text-on-surface font-medium cursor-pointer disabled:cursor-not-allowed"
              id="edit-objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              disabled={isSubmitting}
            >
              {OBJECTIVE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              name="expand_more"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
